import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Mail, MessageCircle, Send, FileText, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = () => ({
  client_id: "", task_id: null, gst_enabled: true, gst_type: "intra", gst_rate: 18,
  line_items: [{ description: "", hsn: "9982", quantity: 1, rate: 0 }],
  notes: "", terms: "",
});

export default function Invoices() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.specialization === "accounts";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [search] = useSearchParams();

  const load = async () => setItems((await api.get("/invoices")).data);
  const loadRefs = async () => {
    const [c, t] = await Promise.all([api.get("/clients"), api.get("/tasks")]);
    setClients(c.data); setTasks(t.data);
  };
  useEffect(() => { load(); loadRefs(); }, []);

  // Prefill from ?task_id=... when navigated from Tasks
  useEffect(() => {
    const tid = search.get("task_id");
    if (!tid || !tasks.length) return;
    const t = tasks.find(x => x.id === tid);
    if (!t) return;
    setEditing(null);
    setForm({
      ...empty(),
      task_id: t.id,
      client_id: t.client_id,
      line_items: [{ description: `${t.service} — ${t.client_name}`, hsn: "9982", quantity: 1, rate: 0 }],
    });
    setOpen(true);
  }, [search, tasks]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = async (row) => {
    const { data } = await api.get(`/invoices/${row.id}`);
    setEditing(data);
    setForm({
      client_id: data.client_id, task_id: data.task_id,
      gst_enabled: data.gst_enabled, gst_type: data.gst_type, gst_rate: data.gst_rate,
      line_items: data.line_items.map(i => ({ ...i })),
      notes: data.notes || "", terms: data.terms || "",
    });
    setOpen(true);
  };

  const subtotal = (form.line_items || []).reduce((a, it) => a + (parseFloat(it.quantity || 0) * parseFloat(it.rate || 0)), 0);
  const gstAmt = form.gst_enabled ? subtotal * (parseFloat(form.gst_rate || 0)) / 100 : 0;
  const total = subtotal + gstAmt;

  const save = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.line_items.length) { toast.error("Add at least one line item"); return; }
    // Strip client-side derived 'amount' — backend always recomputes qty*rate
    const cleanItems = form.line_items.map(({ description, hsn, quantity, rate }) => ({
      description, hsn: hsn || "",
      quantity: parseFloat(quantity) || 0, rate: parseFloat(rate) || 0,
    }));
    try {
      if (editing) {
        await api.patch(`/invoices/${editing.id}`, {
          line_items: cleanItems, gst_enabled: form.gst_enabled,
          gst_type: form.gst_type, gst_rate: parseFloat(form.gst_rate),
          notes: form.notes, terms: form.terms,
        });
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", {
          client_id: form.client_id, task_id: form.task_id,
          line_items: cleanItems, gst_enabled: form.gst_enabled,
          gst_type: form.gst_type, gst_rate: parseFloat(form.gst_rate),
          notes: form.notes, terms: form.terms,
        });
        toast.success("Invoice created");
      }
      setOpen(false); setEditing(null);
      await load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.invoice_number}?`)) return;
    await api.delete(`/invoices/${row.id}`);
    toast.success("Deleted"); load();
  };

  const pdfUrl = (row) => `${API_BASE}/invoices/${row.id}/pdf?share=${row.share_token}`;

  const sendEmail = async (row) => {
    try {
      const { data } = await api.post(`/invoices/${row.id}/send/email`, {});
      if (data.ok) toast.success(`Emailed to ${data.to}`);
      else toast.error("Email failed", { description: data.error || "Provider rejected the recipient" });
      load();
    } catch (e) { toast.error("Email failed", { description: e.response?.data?.detail || e.message }); }
  };
  const sendWhatsapp = async (row) => {
    try {
      const { data } = await api.post(`/invoices/${row.id}/send/whatsapp`, {});
      if (data.link) {
        window.open(data.link, "_blank");
        toast.success("WhatsApp opened", { description: "Click Send in WhatsApp to deliver." });
      } else {
        toast.success("WhatsApp sent via Twilio");
      }
      load();
    } catch (e) { toast.error("WhatsApp failed", { description: e.response?.data?.detail || e.message }); }
  };
  const sendSms = async (row) => {
    try {
      const { data } = await api.post(`/invoices/${row.id}/send/sms`, {});
      toast.success(`SMS sent to ${data.to}`);
      load();
    } catch (e) { toast.error("SMS failed", { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Billing</div>
          <h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">Invoices</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Auto-numbered tax invoices (or non-GST bills) with your logo, GSTIN and bank details. Send by Email · WhatsApp · SMS.
          </p>
        </div>
        {canManage ? (
          <Button data-testid="new-invoice-btn" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            <Plus size={16} className="mr-1" /> New Invoice
          </Button>
        ) : null}
      </header>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="invoices-mobile-list">
        {items.map(row => (
          <div key={row.id} data-testid={`invoice-card-${row.id}`} className="bg-white border border-zinc-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-zinc-500">{row.invoice_number}</div>
                <div className="font-semibold text-sm truncate">{row.client_name}</div>
              </div>
              <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 ${row.status === "sent" ? "bg-blue-100 text-blue-800" : row.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-800"}`}>{row.status}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
              <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Issue</span><div className="tabular">{row.issue_date}</div></div>
              <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Due</span><div className="tabular">{row.due_date}</div></div>
              <div className="col-span-2 border-t border-zinc-100 pt-1 mt-1 flex items-center justify-between">
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{row.gst_enabled ? `${row.gst_rate}% ${row.gst_type.toUpperCase()}` : "No GST"}</span>
                <span data-testid={`invoice-total-${row.id}-m`} className="font-heading font-bold tabular">₹{row.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1">
              <a data-testid={`invoice-pdf-${row.id}-m`} href={pdfUrl(row)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-zinc-100 rounded"><FileText size={15} /></a>
              {canManage ? (<>
              <button data-testid={`invoice-email-${row.id}-m`} onClick={() => sendEmail(row)} className="p-1.5 hover:bg-blue-50 rounded"><Mail size={15} className="text-blue-700" /></button>
              <button data-testid={`invoice-whatsapp-${row.id}-m`} onClick={() => sendWhatsapp(row)} className="p-1.5 hover:bg-emerald-50 rounded"><MessageCircle size={15} className="text-emerald-700" /></button>
              <button data-testid={`invoice-sms-${row.id}-m`} onClick={() => sendSms(row)} className="p-1.5 hover:bg-amber-50 rounded"><Send size={15} className="text-amber-700" /></button>
              <button data-testid={`invoice-edit-${row.id}-m`} onClick={() => openEdit(row)} className="p-1.5 hover:bg-zinc-100 rounded text-[10px] font-bold px-2">EDIT</button>
              <button data-testid={`invoice-delete-${row.id}-m`} onClick={() => del(row)} className="p-1.5 hover:bg-rose-50 rounded ml-auto"><Trash2 size={15} className="text-rose-600" /></button>
              </>) : null}
            </div>
          </div>
        ))}
        {!items.length && <div className="text-center py-8 text-sm text-zinc-500">No invoices yet.</div>}
      </div>

      <div className="hidden sm:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
              {["#", "Client", "Issue", "Due", "Total", "GST", "Status", "Actions"].map(h => (
                <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="stripe-rows">
            {items.map(row => (
              <tr key={row.id} data-testid={`invoice-row-${row.id}`} className="border-t border-zinc-100">
                <td className="px-3 py-2.5 font-mono text-xs">{row.invoice_number}</td>
                <td className="px-3 py-2.5 font-medium">{row.client_name}</td>
                <td className="px-3 py-2.5 tabular text-xs">{row.issue_date}</td>
                <td className="px-3 py-2.5 tabular text-xs">{row.due_date}</td>
                <td data-testid={`invoice-total-${row.id}`} className="px-3 py-2.5 tabular font-semibold">₹{row.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-xs">{row.gst_enabled ? `${row.gst_rate}% ${row.gst_type.toUpperCase()}` : "—"}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ${row.status === "sent" ? "bg-blue-100 text-blue-800" : row.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-800"}`}>{row.status}</span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <a data-testid={`invoice-pdf-${row.id}`} href={pdfUrl(row)} target="_blank" rel="noreferrer" title="Open PDF" className="p-1 hover:bg-zinc-100 rounded"><FileText size={15} /></a>
                    {canManage ? (<>
                    <button data-testid={`invoice-email-${row.id}`} onClick={() => sendEmail(row)} title="Send email" className="p-1 hover:bg-blue-50 rounded"><Mail size={15} className="text-blue-700" /></button>
                    <button data-testid={`invoice-whatsapp-${row.id}`} onClick={() => sendWhatsapp(row)} title="Send WhatsApp" className="p-1 hover:bg-emerald-50 rounded"><MessageCircle size={15} className="text-emerald-700" /></button>
                    <button data-testid={`invoice-sms-${row.id}`} onClick={() => sendSms(row)} title="Send SMS" className="p-1 hover:bg-amber-50 rounded"><Send size={15} className="text-amber-700" /></button>
                    <button data-testid={`invoice-edit-${row.id}`} onClick={() => openEdit(row)} title="Edit" className="p-1 hover:bg-zinc-100 rounded text-xs font-semibold px-2">EDIT</button>
                    <button data-testid={`invoice-delete-${row.id}`} onClick={() => del(row)} title="Delete" className="p-1 hover:bg-rose-50 rounded"><Trash2 size={15} className="text-rose-600" /></button>
                    </>) : null}
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={8} className="text-center py-12 text-sm text-zinc-500">No invoices yet. Click <b>New Invoice</b> to generate one.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">{editing ? `Edit ${editing.invoice_number}` : "New Invoice"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Add line items, toggle GST (with intra/inter-state), and generate a PDF that includes your logo and bank details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="sm:col-span-2">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-start sm:justify-end gap-4">
                <div className="flex items-center gap-2">
                  <Switch data-testid="invoice-gst-switch" checked={form.gst_enabled} onCheckedChange={(v) => setForm({ ...form, gst_enabled: v })} />
                  <span className="text-xs font-semibold uppercase tracking-wider">GST</span>
                </div>
                {form.gst_enabled ? (
                  <>
                    <div>
                      <Label className="text-[10px] tracking-[0.14em] uppercase font-semibold">Type</Label>
                      <Select value={form.gst_type} onValueChange={(v) => setForm({ ...form, gst_type: v })}>
                        <SelectTrigger data-testid="invoice-gst-type" className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="intra">Intra (CGST+SGST)</SelectItem>
                          <SelectItem value="inter">Inter (IGST)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.14em] uppercase font-semibold">Rate %</Label>
                      <Input data-testid="invoice-gst-rate" className="w-20" type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} />
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {/* Line items */}
            <div className="border border-zinc-200 overflow-x-auto">
              <div className="hidden sm:grid grid-cols-[1fr_100px_60px_100px_100px_30px] gap-2 bg-zinc-50 px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500 min-w-[560px]">
                <div>Description</div><div>HSN/SAC</div><div>Qty</div><div>Rate</div><div>Amount</div><div></div>
              </div>
              {form.line_items.map((it, i) => {
                const amt = (parseFloat(it.quantity || 0) * parseFloat(it.rate || 0)) || 0;
                return (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_100px_60px_100px_100px_30px] sm:min-w-[560px] gap-2 px-3 py-2 border-t border-zinc-100 items-center">
                    <div className="col-span-2 sm:col-span-1"><Label className="sm:hidden text-[9px] tracking-widest uppercase text-zinc-500 font-semibold">Description</Label><Input data-testid={`line-desc-${i}`} value={it.description} onChange={(e) => { const l = [...form.line_items]; l[i] = { ...l[i], description: e.target.value }; setForm({ ...form, line_items: l }); }} /></div>
                    <div><Label className="sm:hidden text-[9px] tracking-widest uppercase text-zinc-500 font-semibold">HSN</Label><Input data-testid={`line-hsn-${i}`} value={it.hsn || ""} onChange={(e) => { const l = [...form.line_items]; l[i] = { ...l[i], hsn: e.target.value }; setForm({ ...form, line_items: l }); }} /></div>
                    <div><Label className="sm:hidden text-[9px] tracking-widest uppercase text-zinc-500 font-semibold">Qty</Label><Input data-testid={`line-qty-${i}`} type="number" value={it.quantity} onChange={(e) => { const l = [...form.line_items]; l[i] = { ...l[i], quantity: e.target.value }; setForm({ ...form, line_items: l }); }} /></div>
                    <div><Label className="sm:hidden text-[9px] tracking-widest uppercase text-zinc-500 font-semibold">Rate</Label><Input data-testid={`line-rate-${i}`} type="number" value={it.rate} onChange={(e) => { const l = [...form.line_items]; l[i] = { ...l[i], rate: e.target.value }; setForm({ ...form, line_items: l }); }} /></div>
                    <div className="tabular font-semibold text-right pr-2 col-span-2 sm:col-span-1 border-t sm:border-0 border-zinc-100 pt-1 sm:pt-0 flex justify-between sm:block"><span className="sm:hidden text-[9px] tracking-widest uppercase text-zinc-500 font-semibold">Amount</span>₹{amt.toFixed(2)}</div>
                    <button data-testid={`line-remove-${i}`} onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, j) => j !== i) })} className="p-1 hover:bg-rose-50 rounded col-span-2 sm:col-span-1 justify-self-end" title="Remove"><X size={14} className="text-rose-600" /></button>
                  </div>
                );
              })}
              <div className="px-3 py-2 border-t border-zinc-100">
                <Button data-testid="line-add-btn" variant="outline" onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: "", hsn: "9982", quantity: 1, rate: 0 }] })} className="rounded-none border-zinc-300">
                  <Plus size={14} className="mr-1" /> Add line
                </Button>
              </div>
            </div>

            {/* Totals */}
            <div className="border border-zinc-200 bg-zinc-50 p-4">
              <div className="grid grid-cols-2 gap-y-1 text-sm max-w-sm ml-auto">
                <div className="text-zinc-600">Subtotal</div>
                <div className="text-right tabular">₹{subtotal.toFixed(2)}</div>
                {form.gst_enabled ? (
                  <>
                    <div className="text-zinc-600">{form.gst_type === "inter" ? `IGST ${form.gst_rate}%` : `CGST + SGST @ ${form.gst_rate/2}% each`}</div>
                    <div className="text-right tabular">₹{gstAmt.toFixed(2)}</div>
                  </>
                ) : null}
                <div className="col-span-2 border-t border-zinc-300 mt-2 pt-2 font-heading font-black text-lg flex justify-between">
                  <span>Total</span><span className="tabular" data-testid="invoice-total-preview">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Footer notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Leave blank to use default from Settings" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Terms</Label>
                <Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Leave blank to use default from Settings" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="invoice-save-btn" onClick={save} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
              {editing ? "Save changes" : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
